import { FIX_TYPE } from "@clover/ashe-core-editor";
import { RenderComponent, RuntimeCenterClass, runtimeCenterSymbol } from "@clover/ashe-core-runtime";
import { createApp, defineComponent, inject, provide, reactive, ref, shallowReactive, Suspense, Transition, computed, onMounted, onUnmounted } from "vue";
import { sendLog } from "../libs/sendLog";
const SlotItem = defineComponent({
  props: {
    // element 信息需要转vw
    layout: String,
    element: Object,
    panel: Object,
    appData: Object,
    panelStore: Object,
    suspenseSubject: Object,
  },
  setup(props) {
    provide("__page_id", props.appData.pageId);

    const nodeStyle: Record<string, any> = reactive({
      position: "absolute",
      width: props.element?.width,
      height: props.element?.height,
      top: props.element?.top,
      left: props.element?.left,
      transform: `rotate(${props.element?.rotate}deg)`,
      zIndex: props.element?.zIndex,
      overflow: "hidden",
      ...(props.element?.extraStyle || {}),
    });

    const getNodeProps = (nodeId: string) => {
      return Object.assign(props.panelStore.extraProps[nodeId] || {}, props.panelStore.prop[nodeId]);
    };

    const runtimeCenter = shallowReactive(inject<RuntimeCenterClass>(runtimeCenterSymbol));
    const runtimeActivityData = shallowReactive(
      inject<{
        liked: boolean;
        likes: number;
      }>("runtimeActivityData")
    );

    const slots = props.element.children?.map((child) => {
      const el = document.createElement("div");

      const slotItem = createApp(SlotItem, {
        element: child,
        panel: getNodeProps(child.id),
        appData: props.appData,
        panelStore: props.panelStore,
        layout: props.layout,
      });
      slotItem.provide("runtimeActivityData", runtimeActivityData);
      slotItem.provide(runtimeCenterSymbol, runtimeCenter);
      slotItem.provide("__page_id", props.appData.pageId);
      slotItem.mount(el);
      return el.childNodes[0] as Element;
    });

    if (props.layout === "flex") {
      switch (props.element.fixType) {
        case FIX_TYPE.UNSET:
          nodeStyle.position = "relative";
          nodeStyle.marginLeft = props.element?.left;
          nodeStyle.marginTop = props.element?.top;
          delete nodeStyle.left;
          delete nodeStyle.top;
          break;
        case FIX_TYPE.ABSOLUTE:
          nodeStyle.position = "absolute";
          break;
      }
    }

    switch (props.element.fixType) {
      case FIX_TYPE.TOP:
        nodeStyle.position = "fixed";
        nodeStyle.top = props.element.fixValue;
        break;
      case FIX_TYPE.BOTTOM:
        nodeStyle.position = "fixed";
        nodeStyle.bottom = props.element.fixValue;
        delete nodeStyle.top;
        break;
    }

    const widgetVisible = ref(props.panel.widgetShow !== "0");

    const nodeProps = computed<{
      displayName: string;
      id: string;
      pageId: string;
      pageIdEcpt: string;
      activityId: string;
      activityIdEcpt: string;
      widgetType: string;
    }>(() => {
      const extraProps = getNodeProps(props.element.id)
      return {
        id: props.element.id,
        displayName: `${extraProps.pageId}-${extraProps.displayName}`,
        pageId: extraProps.pageId,
        pageIdEcpt: extraProps.pageIdEcpt,
        activityId: extraProps.activityId,
        activityIdEcpt: extraProps.activityIdEcpt,
        widgetType: props.element.component?.widgetId,
      }
    })

    // IntersectionObserver 曝光埋点
    const elementRef = ref<HTMLElement | null>(null);
    let observer: IntersectionObserver | null = null;
    let hasExposed = false; // 标记是否已经曝光过，避免重复上报


    // 点击事件处理函数
    const handleClick = () => {
      try {
        sendLog("clk", "activity_widget_clk", {
          page_id: props.appData.pageId,
          widget_type: nodeProps.value?.widgetType,
          widget_id: props.element.id,
          widget_name: nodeProps.value?.displayName,
        });
      } catch (error) {
        console.error('handleClick sendLog error', error)
      }
    };

    onMounted(() => {
      if (!elementRef.value) return;

      try {
        // 创建 IntersectionObserver
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // 当元素进入视口且可见性大于 50% 时，触发曝光埋点
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6 && !hasExposed) {
                hasExposed = true;
                sendLog("exp", "activity_widget_exp", {
                  page_id: props.appData.pageId,
                  widget_type: nodeProps.value?.widgetType,
                  widget_id: props.element.id,
                  widget_name: nodeProps.value?.displayName,
                });
  
              }
            });
          },
          {
            threshold: 0.5, // 当元素 50% 可见时触发
            rootMargin: "0px", // 可以设置边距，提前或延后触发
          }
        );
  
        // 开始观察元素
        observer.observe(elementRef.value);

        // 添加原生 DOM 事件监听器，使用 capture 模式
        elementRef.value.addEventListener("click", handleClick, { capture: true });
      } catch (error) {
        console.error('onMounted IntersectionObserver error', error)
      }
    });

    onUnmounted(() => {
      // 清理 IntersectionObserver
      if (observer && elementRef.value) {
        observer.unobserve(elementRef.value);
        observer.disconnect();
        observer = null;
      }
      // 移除事件监听器
      if (elementRef.value) {
        elementRef.value.removeEventListener("click", handleClick, { capture: true });
      }
    });


    return () => (
      <Transition name={props.layout === "flex" && props.element.fixType === 0 ? "slot" : ""}>
        <div
          ref={elementRef}
          class="__slot-item"
          v-show={widgetVisible.value}
          style={{ ...nodeStyle }}
          data-display-name={nodeProps.value?.displayName}
          data-page-id={props.appData.pageId}
          data-element-id={props.element.id}
        >
          <Suspense onResolve={() => props.suspenseSubject?.next()}>
            <RenderComponent
              element={props.element as any}
              panel={props.panel}
              slots={slots}
              onVisibleChange={(visible: boolean) => {
                widgetVisible.value = visible;
              }}
            />
          </Suspense>
        </div>
      </Transition>
    );
  },
});

export default SlotItem;
